import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Input } from "@/components/ui/input";
import { Users } from "lucide-react";

import AddressFieldset from "@/components/AddressFieldset";
import Form from "@/components/Form";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import { CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useCopyFormFields } from "@/hooks/useCopyFormFields";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import { writeClient } from "@/schemas/client";
import { ApiError } from "@/services/api";
import { ReadClient, WriteClient } from "@/types/client";
import { CheckedState } from "@radix-ui/react-checkbox";
import { UseMutateFunction } from "@tanstack/react-query";

interface ClientFormProps {
  client?: Partial<ReadClient>;
  onSubmit: UseMutateFunction<ReadClient, ApiError, WriteClient>;
  disabled?: boolean;
}

const defaultClient: Partial<ReadClient> = {
  name: "",
  tax_number: "",
  city: "",
  street_name: "",
  street_number: "",
  apartment_number: "",
  postal_code: "",
  city_invoice: "",
  street_name_invoice: "",
  street_number_invoice: "",
  apartment_number_invoice: "",
  postal_code_invoice: "",
};

const ClientForm = ({
  client = defaultClient,
  onSubmit,
  disabled,
}: ClientFormProps) => {
  const copyEnabled = hasSameAddress(client);
  const [copyCompanyAddress, setCopyCompanyAddress] = useState(copyEnabled);
  const { t } = useTranslation();
  const addressFields = useRef<[Path<WriteClient>, Path<WriteClient>][]>([
    ["city", "city_invoice"],
    ["street_name", "street_name_invoice"],
    ["street_number", "street_number_invoice"],
    ["postal_code", "postal_code_invoice"],
    ["apartment_number", "apartment_number_invoice"],
    ["contact_person", "contact_person_invoice"],
    ["email", "email_invoice"],
    ["phone_number", "phone_number_invoice"],
  ]);

  const form = useForm<WriteClient>({
    resolver: zodResolver(writeClient),
    defaultValues: client,
    disabled,
  });

  useFormUnsavedChangesWarning(form);

  const copyValues = useCopyFormFields(
    addressFields.current,
    form,
    copyCompanyAddress,
  );

  useEffect(() => {
    if (copyCompanyAddress) {
      copyValues();
    }
  }, [copyCompanyAddress, copyValues]);

  const toggleSameAsCompanyAddress = useCallback(
    (checked: CheckedState) => {
      setCopyCompanyAddress(!!checked);
      // Only clear fields when manually unchecking
      if (!checked) {
        addressFields.current.forEach(([, field]) => {
          form.setValue(field, "");
        });
      }
    },
    [form],
  );

  const disableSetSameAsCompanyAddress = useCallback(() => {
    setCopyCompanyAddress(false);
    // Don't clear fields when typing disables copy
  }, []);

  const handleSubmit = async (values: WriteClient) => {
    onSubmit(values);
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent
        icon={<Users className="w-6 h-6 mr-3" />}
        footer={<SaveButton />}
        title={t("clients.title.single")}
        subtitle={
          client.ref_number !== undefined && (
            <>
              {t("clients.fields.ref_number")}:{" "}
              <strong>{client.ref_number?.toString()}</strong>
            </>
          )
        }
      >
        <Fieldset className="grid lg:grid-cols-2 grid-cols-1 gap-4">
          <Field
            name="name"
            label={t("clients.fields.name")}
            required={isRequired(writeClient.shape.name)}
            render={({ field }) => <Input {...field} />}
          />
          <Field
            name="code"
            label={t("clients.fields.code")}
            required={isRequired(writeClient.shape.code)}
            render={({ field }) => <Input {...field} />}
          />
          <Field
            name="tax_number"
            label={t("clients.fields.tax_number")}
            required={isRequired(writeClient.shape.tax_number)}
            render={({ field }) => <Input {...field} />}
          />
          <Field
            name="regon_number"
            label={t("clients.fields.regon_number")}
            required={isRequired(writeClient.shape.regon_number)}
            render={({ field }) => <Input {...field} />}
          />
        </Fieldset>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <header className="flex flex-row justify-between space-y-0 pt-4">
              <CardTitle level={4}>{t("clients.addressSection")}</CardTitle>
            </header>
            <AddressFieldset>
              <AddressFieldset.PostalCode />
              <AddressFieldset.City />
              <AddressFieldset.StreetName />
              <AddressFieldset.StreetNumber />
              <AddressFieldset.ApartmentNumber />
              <AddressFieldset.ContactPerson />
              <AddressFieldset.Email />
              <AddressFieldset.PhoneNumber />
            </AddressFieldset>
          </div>
          <div>
            <header className="flex flex-row items-center justify-between space-y-0 pt-4">
              <CardTitle level={4}>
                {t("clients.invoiceAddressSection")}
              </CardTitle>
              <Field
                className="flex flex-row-reverse"
                name="sameAsCompanyAddress"
                label={t("addressFieldset.sameAsAddress")}
                render={() => (
                  <Checkbox
                    checked={copyCompanyAddress}
                    onCheckedChange={toggleSameAsCompanyAddress}
                    disabled={disabled}
                  />
                )}
              />
            </header>
            <AddressFieldset
              schema={writeClient}
              postfix="_invoice"
              {...(copyCompanyAddress && {
                onChange: disableSetSameAsCompanyAddress,
              })}
            >
              <AddressFieldset.PostalCode />
              <AddressFieldset.City />
              <AddressFieldset.StreetName />
              <AddressFieldset.StreetNumber />
              <AddressFieldset.ApartmentNumber />
              <AddressFieldset.ContactPerson />
              <AddressFieldset.Email />
              <AddressFieldset.PhoneNumber />
            </AddressFieldset>
          </div>
        </div>
      </PageContent>
    </Form>
  );
};

export default ClientForm;

const hasSameAddress = (client: Partial<ReadClient>) => {
  return (
    client.city === client.city_invoice &&
    client.street_name === client.street_name_invoice &&
    client.street_number === client.street_number_invoice &&
    client.apartment_number === client.apartment_number_invoice &&
    client.postal_code === client.postal_code_invoice
  );
};
